import { Star } from "lucide-react";

// Echte Google reviews van Fresh View (Leuven)
// Bron: Google Business Profile - 4.8 gemiddeld op basis van 32 reviews
const GOOGLE_REVIEWS_URL = "https://www.google.com/search?q=fresh+view+reviews";

const testimonials = [
  {
    name: "Hilde De Swaef",
    rating: 5,
    time: "1 week geleden",
    text: "Wij zijn heel tevreden met onze schitterende vensters! Thibau levert super degelijk werk: totaal vlek- en streeploze ruiten (enkel buitenkant) en vriendelijke service.",
    service: "Ramen wassen",
  },
  {
    name: "Frank Goditiabois",
    rating: 5,
    time: "1 week geleden",
    text: "Gevel- en vensterreiniging. Top werk, goede communicatie. Aanbevolen!",
    service: "Gevel en ramen",
  },
  {
    name: "Dorothea De Bleeckere",
    rating: 5,
    time: "2 weken geleden",
    text: "Thibau is super vriendelijk en professioneel. De ramen blinken en ook de raamkozijnen zijn mee gedaan! Heel tevreden!",
    service: "Ramen wassen",
  },
  {
    name: "Leentje Vandeput",
    rating: 5,
    time: "5 maanden geleden",
    text: "Na verbouwingswerken waren onze ramen extreem vuil. Fresh View heeft onze ramen weer laten schitteren. We zijn zeer tevreden, een echte aanrader. Zeer vriendelijk en correcte prijsofferte. Op tijd aanwezig zoals afgesproken.",
    service: "Ramen wassen",
  },
  {
    name: "Ruth Cloots",
    rating: 5,
    time: "4 maanden geleden",
    text: "Makkelijk contact, snelle service, blinkende ramen en zonnepanelen.",
    service: "Ramen & zonnepanelen",
  },
  {
    name: "Bart Vanmeerbeek",
    rating: 5,
    time: "6 maanden geleden",
    text: "Zorgvuldig en attent: netjes gepoetste ramen met aandacht om het huis proper te houden! Een aanrader om in huis te vragen! Bedankt Thibau!",
    service: "Ramen wassen",
  },
];

const AVG_RATING = 4.8;
const REVIEW_COUNT = 32;

// Google "G" logo icon
const GoogleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);

export default function TestimonialsSection() {
  return (
    <section className="py-12 sm:py-20 bg-white" aria-labelledby="reviews-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-8 sm:mb-12">
          <span className="text-sky-600 font-semibold text-xs sm:text-sm tracking-wide">
            Klantbeoordelingen
          </span>
          <h2
            id="reviews-heading"
            className="text-2xl sm:text-4xl font-bold text-slate-900 mt-2 sm:mt-3 mb-3 sm:mb-4"
            style={{ fontFamily: "Instrument Sans, sans-serif" }}
          >
            Wat onze klanten zeggen.
          </h2>

          {/* Real Google rating badge */}
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 sm:gap-3 px-3 py-2 rounded-full border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm transition-all"
            data-testid="google-rating-badge"
            aria-label={`${AVG_RATING} van 5 sterren op basis van ${REVIEW_COUNT} Google reviews`}
          >
            <GoogleIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-slate-800 font-bold text-sm sm:text-base">{AVG_RATING.toString().replace('.', ',')}</span>
            <div className="flex" aria-hidden="true">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
                    i <= Math.round(AVG_RATING) ? "fill-amber-400 text-amber-400" : "text-slate-300"
                  }`}
                />
              ))}
            </div>
            <span className="text-slate-500 text-xs sm:text-sm">
              {REVIEW_COUNT} Google reviews
            </span>
          </a>
        </div>

        {/* Reviews grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {testimonials.map((t, i) => (
            <article
              key={i}
              className="relative p-5 sm:p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:shadow-md transition-all"
              data-testid={`testimonial-${i}`}
            >
              {/* Google mark - top right */}
              <GoogleIcon className="absolute top-4 right-4 w-4 h-4 sm:w-5 sm:h-5 opacity-70" />

              <div className="flex mb-3" aria-label={`${t.rating} van 5 sterren`}>
                {Array.from({ length: t.rating }).map((_, idx) => (
                  <Star key={idx} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>

              <p className="text-slate-700 text-sm sm:text-base leading-relaxed mb-4">
                &ldquo;{t.text}&rdquo;
              </p>

              <div className="flex items-center justify-between pt-3 border-t border-slate-200 gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.time}</p>
                </div>
                <span className="text-[11px] sm:text-xs px-2 py-1 rounded-full bg-white text-slate-600 border border-slate-200 whitespace-nowrap flex-shrink-0">
                  {t.service}
                </span>
              </div>
            </article>
          ))}
        </div>

        {/* CTA to Google */}
        <div className="mt-8 sm:mt-10">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-semibold text-sm sm:text-base"
            data-testid="view-all-reviews-link"
          >
            Bekijk alle {REVIEW_COUNT} reviews op Google
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
